import React, { useState, useMemo } from 'react';
import { Table, Input } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { ValidationMetric } from '../../../../../types/scorecard';
import { ADOPTION_COLORS } from '../../../../constants/graphColors';

interface ValidationMetricsProps {
  data: ValidationMetric[];
}

const ValidationMetrics: React.FC<ValidationMetricsProps> = ({ data }) => {
  const { t } = useTranslation();
  const [searchValue, setSearchValue] = useState('');

  const filteredData = useMemo(() => {
    if (!data) return [];
    if (!searchValue.trim()) return data;

    const searchLower = searchValue.toLowerCase().trim();
    return data.filter(item => item.validation.toLowerCase().includes(searchLower));
  }, [data, searchValue]);

  const columns = [
    {
      title: t('receiver:validation'),
      dataIndex: 'validation',
      key: 'validation',
      width: 180,
      sorter: (a: ValidationMetric, b: ValidationMetric) =>
        a.validation.localeCompare(b.validation),
      render: (text: string) => (
        <div className="text-xs font-medium" title={text}>
          {text}
        </div>
      ),
    },
    {
      title: t('receiver:fulfilled'),
      dataIndex: 'fulfilled',
      key: 'fulfilled',
      width: 60,
      align: 'center' as const,
      sorter: (a: ValidationMetric, b: ValidationMetric) => a.fulfilled - b.fulfilled,
      render: (value: number) => (
        <div className="text-xs font-bold" style={{ color: ADOPTION_COLORS.SUCCESS }}>
          {value.toLocaleString()}
        </div>
      ),
    },
    {
      title: t('receiver:notFulfilled'),
      dataIndex: 'notFulfilled',
      key: 'notFulfilled',
      width: 80,
      align: 'center' as const,
      sorter: (a: ValidationMetric, b: ValidationMetric) => a.notFulfilled - b.notFulfilled,
      render: (value: number) => (
        <div className="text-xs font-bold" style={{ color: ADOPTION_COLORS.ERROR }}>
          {value.toLocaleString()}
        </div>
      ),
    },
  ];

  // Calculate totals from filtered data
  const totals = filteredData.reduce(
    (acc, item) => ({
      fulfilled: acc.fulfilled + item.fulfilled,
      notFulfilled: acc.notFulfilled + item.notFulfilled,
    }),
    { fulfilled: 0, notFulfilled: 0 }
  );

  // Add total row
  const dataWithTotal = [
    ...filteredData,
    {
      validation: t('receiver:total'),
      fulfilled: totals.fulfilled,
      notFulfilled: totals.notFulfilled,
    },
  ];

  if (!data || data.length === 0) {
    return (
      <div
        className="flex items-center justify-center h-24 text-center"
        style={{ color: ADOPTION_COLORS.TEXT_SECONDARY }}
      >
        {t('receiver:noValidationData')}
      </div>
    );
  }

  return (
    <div>
      <div className="mb-3">
        <Input
          placeholder={t('receiver:searchByValidation')}
          prefix={<SearchOutlined />}
          value={searchValue}
          onChange={e => setSearchValue(e.target.value)}
          size="small"
          allowClear
        />
      </div>
      <Table
        dataSource={dataWithTotal}
        columns={columns}
        pagination={false}
        size="small"
        rowKey="validation"
        scroll={{ x: 'max-content' }}
        rowClassName={record => {
          if (record.validation === t('receiver:total')) {
            return 'bg-gray-50 font-semibold border-t-2';
          }
          return '';
        }}
        locale={{
          emptyText: searchValue.trim()
            ? t('receiver:noMatchingValidations')
            : t('receiver:noValidationData'),
        }}
      />
    </div>
  );
};

export default ValidationMetrics;
